import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Event, GuestQuestion, GuestQuestionAnswers } from '../../lib/types';
import {
  getActiveGuestQuestionOptions,
  getActiveGuestQuestions,
  isValidGuestQuestionAnswer,
} from '../../lib/guestQuestions';
import { getMaxGuestCount, validateGuestCount } from '../../lib/utils';
import { trackError } from '../../lib/analytics';
import GuestCounter from './GuestCounter';
import PhoneInput from '../PhoneInput';

export type RsvpStatus = 'attending' | 'maybe' | 'notattending';
export interface GuidedRsvpData {
  status: RsvpStatus;
  firstName: string;
  lastName: string;
  phone: string;
  message: string;
  adultCount: number;
  childCount: number;
  publicGuestView: boolean;
  guestQuestionAnswers: GuestQuestionAnswers;
}
type WizardStep =
  | { id: 'status'; type: 'status' }
  | { id: 'party'; type: 'party' }
  | { id: string; type: 'question'; question: GuestQuestion }
  | { id: 'contact'; type: 'contact' }
  | { id: 'note'; type: 'note' };

interface GuidedRsvpModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  initialStatus?: RsvpStatus | null;
  initialFirstName?: string;
  initialLastName?: string;
  initialPhone?: string;
  initialMessage?: string;
  initialAdultCount?: number;
  initialChildCount?: number;
  initialPublicGuestView?: boolean;
  initialGuestQuestionAnswers?: GuestQuestionAnswers;
  requireContactForAllStatuses?: boolean;
  completionMessage?: string;
  onStatusChange?: (status: RsvpStatus) => void;
  errorContext?: {
    flow: 'invite' | 'public';
    inviteId?: string;
  };
  onSubmit: (data: GuidedRsvpData) => Promise<unknown>;
  onComplete: (data: GuidedRsvpData, result: unknown) => void;
}

export default function GuidedRsvpModal({
  isOpen,
  onClose,
  event,
  initialStatus = null,
  initialFirstName = '',
  initialLastName = '',
  initialPhone = '',
  initialMessage = '',
  initialAdultCount = 1,
  initialChildCount = 0,
  initialPublicGuestView = true,
  initialGuestQuestionAnswers = {},
  requireContactForAllStatuses = false,
  completionMessage = 'Your RSVP was sent to the host.',
  onStatusChange,
  errorContext,
  onSubmit,
  onComplete,
}: GuidedRsvpModalProps) {
  const { t } = useTranslation();
  const guestQuestions = getActiveGuestQuestions(event.event_questions);
  const [selectedStatus, setSelectedStatus] = useState<RsvpStatus | null>(initialStatus);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [transitionKey, setTransitionKey] = useState(0);
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [phone, setPhone] = useState(initialPhone);
  const [message, setMessage] = useState(initialMessage);
  const [adultCount, setAdultCount] = useState(initialAdultCount);
  const [childCount, setChildCount] = useState(initialChildCount);
  const [publicGuestView, setPublicGuestView] = useState(initialPublicGuestView);
  const [guestQuestionAnswers, setGuestQuestionAnswers] = useState<GuestQuestionAnswers>(initialGuestQuestionAnswers);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const completionTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const wasOpenRef = useRef(isOpen);

  const steps = useMemo<WizardStep[]>(() => {
    const statusStep: WizardStep = { id: 'status', type: 'status' };
    if (!selectedStatus) return [statusStep];

    if (selectedStatus !== 'attending') {
      return requireContactForAllStatuses
        ? [statusStep, { id: 'contact', type: 'contact' }, { id: 'note', type: 'note' }]
        : [statusStep, { id: 'note', type: 'note' }];
    }

    return [
      statusStep,
      { id: 'party', type: 'party' },
      ...guestQuestions.map((question) => ({
        id: `question-${question.id}`,
        type: 'question' as const,
        question,
      })),
      { id: 'contact', type: 'contact' },
      { id: 'note', type: 'note' },
    ];
  }, [guestQuestions, requireContactForAllStatuses, selectedStatus]);

  const currentStep = steps[Math.min(currentStepIndex, steps.length - 1)];
  const progress = selectedStatus ? ((currentStepIndex + 1) / steps.length) * 100 : 8;
  const hasUnsavedChanges = useMemo(() => (
    selectedStatus !== initialStatus
    || firstName !== initialFirstName
    || lastName !== initialLastName
    || phone !== initialPhone
    || message !== initialMessage
    || adultCount !== initialAdultCount
    || childCount !== initialChildCount
    || publicGuestView !== initialPublicGuestView
    || JSON.stringify(guestQuestionAnswers) !== JSON.stringify(initialGuestQuestionAnswers)
  ), [
    adultCount,
    childCount,
    firstName,
    guestQuestionAnswers,
    initialAdultCount,
    initialChildCount,
    initialFirstName,
    initialGuestQuestionAnswers,
    initialLastName,
    initialMessage,
    initialPhone,
    initialPublicGuestView,
    initialStatus,
    lastName,
    message,
    phone,
    publicGuestView,
    selectedStatus,
  ]);

  useEffect(() => {
    if (isOpen && !isComplete) {
      headingRef.current?.focus({ preventScroll: true });
    }
  }, [currentStepIndex, isComplete, isOpen]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSelectedStatus(initialStatus);
      setCurrentStepIndex(0);
      setDirection('forward');
      setTransitionKey((key) => key + 1);
      setFirstName(initialFirstName);
      setLastName(initialLastName);
      setPhone(initialPhone);
      setMessage(initialMessage);
      setAdultCount(initialAdultCount);
      setChildCount(initialChildCount);
      setPublicGuestView(initialPublicGuestView);
      setGuestQuestionAnswers(initialGuestQuestionAnswers);
      setError('');
      setIsSubmitting(false);
      setIsComplete(false);
      setShowCloseConfirmation(false);
    }
    wasOpenRef.current = isOpen;
  }, [
    initialAdultCount,
    initialChildCount,
    initialFirstName,
    initialGuestQuestionAnswers,
    initialLastName,
    initialMessage,
    initialPhone,
    initialPublicGuestView,
    initialStatus,
    isOpen,
  ]);

  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
    };
  }, []);

  if (!isOpen) return null;

  const requestClose = () => {
    if (hasUnsavedChanges && !isComplete) {
      setShowCloseConfirmation(true);
      return;
    }
    onClose();
  };

  const moveToStep = (index: number, nextDirection: 'forward' | 'back') => {
    setDirection(nextDirection);
    setCurrentStepIndex(index);
    setTransitionKey((key) => key + 1);
    setError('');
  };

  const scheduleAdvance = (targetIndex: number, delay = 240) => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = setTimeout(() => moveToStep(targetIndex, 'forward'), delay);
  };

  const chooseStatus = (status: RsvpStatus) => {
    setSelectedStatus(status);
    onStatusChange?.(status);
    setError('');
    scheduleAdvance(1, 400);
  };

  const updateQuestionAnswer = (questionId: string, value: string, autoAdvance = false) => {
    setGuestQuestionAnswers((answers) => {
      if (!value) {
        const nextAnswers = { ...answers };
        delete nextAnswers[questionId];
        return nextAnswers;
      }
      return { ...answers, [questionId]: value };
    });
    setError('');
    if (autoAdvance) scheduleAdvance(currentStepIndex + 1);
  };

  const validateCurrentStep = () => {
    if (currentStep.type === 'status' && !selectedStatus) {
      setError('Choose a response to continue.');
      return false;
    }

    if (currentStep.type === 'party') {
      const validation = validateGuestCount(event, adultCount, childCount);
      if (!validation.isValid) {
        setError(validation.errorMessage || 'Too many guests.');
        return false;
      }
    }

    if (currentStep.type === 'question') {
      const answer = guestQuestionAnswers[currentStep.question.id];
      if (currentStep.question.required && !isValidGuestQuestionAnswer(currentStep.question, answer)) {
        setError('Please answer this question to continue.');
        return false;
      }
    }

    if (currentStep.type === 'contact' && (!firstName.trim() || !lastName.trim() || !phone.trim())) {
      setError(t('event.rsvp.errors.requiredFields'));
      return false;
    }

    return true;
  };

  const submitRsvp = async () => {
    if (!selectedStatus) return;

    try {
      setIsSubmitting(true);
      setError('');
      const data: GuidedRsvpData = {
        status: selectedStatus,
        firstName,
        lastName,
        phone,
        adultCount,
        childCount,
        message,
        publicGuestView,
        guestQuestionAnswers,
      };
      const result = await onSubmit(data);

      setIsComplete(true);
      completionTimerRef.current = setTimeout(() => onComplete(data, result), 1250);
    } catch (submitError) {
      console.error('RSVP error:', submitError);
      const normalizedError = submitError instanceof Error
        ? submitError
        : new Error(t('event.rsvp.errors.generic'));
      trackError(normalizedError, {
        error_type: 'rsvp_submission_error',
        event_id: event.id,
        invite_id: errorContext?.inviteId,
        rsvp_flow: errorContext?.flow,
        rsvp_status: selectedStatus,
      });
      setError(normalizedError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goNext = async () => {
    if (!validateCurrentStep()) return;
    if (currentStepIndex < steps.length - 1) {
      moveToStep(currentStepIndex + 1, 'forward');
      return;
    }
    await submitRsvp();
  };

  const goBack = () => {
    if (currentStepIndex > 0) moveToStep(currentStepIndex - 1, 'back');
  };

  const maxTotalGuests = getMaxGuestCount(event);
  const maxAdults = Math.max(1, maxTotalGuests - childCount);
  const maxChildren = Math.max(0, maxTotalGuests - adultCount);

  const renderStatusStep = () => {
    const options: Array<{ value: RsvpStatus; label: string; detail: string; icon: string }> = [
      { value: 'attending', label: 'Yes, I’ll be there', detail: 'Count me in', icon: '🎉' },
      ...(event.allow_maybe_option
        ? [{ value: 'maybe' as const, label: 'Maybe', detail: 'I’m not sure yet', icon: '🤔' }]
        : []),
      { value: 'notattending', label: 'Can’t make it', detail: 'I’ll have to pass', icon: '💌' },
    ];

    return (
      <div>
        <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">You’re invited</p>
        <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-bold tracking-tight text-gray-900 outline-none">
          Can you make it?
        </h2>
        <p className="mt-2 text-base text-gray-500">Let the host know your plans.</p>
        <div className="mt-8 grid gap-3">
          {options.map((option) => {
            const selected = selectedStatus === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => chooseStatus(option.value)}
                className={`flex w-full items-center rounded-xl border-2 p-4 text-left transition-all ${
                  selected
                    ? 'border-green-500 bg-green-50 ring-2 ring-green-100'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40'
                }`}
              >
                <span className="mr-4 text-2xl" aria-hidden="true">{option.icon}</span>
                <span className="flex-1">
                  <span className="block font-semibold text-gray-900">{option.label}</span>
                  <span className="mt-0.5 block text-sm text-gray-500">{option.detail}</span>
                </span>
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${selected ? 'border-green-600 bg-green-600 ring-4 ring-green-100' : 'border-gray-300'}`}
                  aria-hidden="true"
                >
                  {selected && (
                    <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPartyStep = () => (
    <div>
      <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">Your party</p>
      <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-bold tracking-tight text-gray-900 outline-none">
        Who’s coming with you?
      </h2>
      <p className="mt-2 text-base text-gray-500">Include yourself in the adult count.</p>
      <div className="mt-8 divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white px-5 shadow-sm">
        <GuestCounter
          label={t('event.rsvp.adults')}
          count={adultCount}
          onIncrement={() => setAdultCount(adultCount + 1)}
          onDecrement={() => setAdultCount(adultCount - 1)}
          minValue={1}
          maxValue={maxAdults}
        />
        {event.childcount && (
          <GuestCounter
            label={t('event.rsvp.children')}
            count={childCount}
            onIncrement={() => setChildCount(childCount + 1)}
            onDecrement={() => setChildCount(childCount - 1)}
            minValue={0}
            maxValue={maxChildren}
          />
        )}
      </div>
      {maxTotalGuests < Number.MAX_SAFE_INTEGER && (
        <p className="mt-4 text-sm text-gray-500">Up to {maxTotalGuests} people can RSVP on this invitation.</p>
      )}
    </div>
  );

  const renderQuestionStep = (question: GuestQuestion) => {
    const answer = guestQuestionAnswers[question.id] || '';
    const activeOptions = getActiveGuestQuestionOptions(question);
    const hasStaleChoice = question.type === 'single_choice'
      && !!answer
      && !activeOptions.some((option) => option.id === answer);
    return (
      <div>
        <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">A question from your host</p>
        <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-bold leading-tight tracking-tight text-gray-900 outline-none">
          {question.prompt}
        </h2>
        <p className="mt-2 text-sm text-gray-500">{question.required ? 'Required' : 'Optional — you can skip this one'}</p>

        {question.type === 'single_choice' ? (
          <div className="mt-8 grid gap-3" role="radiogroup" aria-label={question.prompt}>
            {activeOptions.map((option) => {
              const selected = answer === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => updateQuestionAnswer(question.id, option.id, true)}
                  className={`flex min-h-14 items-center rounded-xl border-2 px-4 py-3 text-left text-base transition-all ${
                    selected
                      ? 'border-blue-500 bg-blue-50 font-semibold text-blue-900 ring-2 ring-blue-100'
                      : 'border-gray-200 bg-white text-gray-800 hover:border-blue-300'
                  }`}
                >
                  <span className={`mr-3 h-5 w-5 rounded-full border-2 ${selected ? 'border-[6px] border-blue-600' : 'border-gray-300'}`} />
                  {option.label}
                </button>
              );
            })}
            {!question.required && answer && (
              <button
                type="button"
                onClick={() => updateQuestionAnswer(question.id, '')}
                className="mt-1 justify-self-start text-sm font-medium text-gray-500 underline decoration-gray-300 underline-offset-4 hover:text-gray-800"
              >
                Clear selection
              </button>
            )}
            {hasStaleChoice && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">
                Your previous choice is no longer available. Please choose another option.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-8">
            <textarea
              value={answer}
              onChange={(event) => updateQuestionAnswer(question.id, event.target.value.slice(0, 200))}
              rows={5}
              maxLength={200}
              placeholder="Type your answer..."
              className="block w-full resize-none border-0 border-b-2 border-gray-300 bg-transparent px-0 py-3 text-xl text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-0"
            />
            <p className="mt-2 text-right text-xs text-gray-400">{answer.length}/200</p>
          </div>
        )}
      </div>
    );
  };

  const renderContactStep = () => (
    <div>
      <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">Almost there</p>
      <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-bold tracking-tight text-gray-900 outline-none">
        How can the host reach you?
      </h2>
      <p className="mt-2 text-base text-gray-500">We’ll use this if event details change.</p>
      <div className="mt-8 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm font-medium text-gray-700">
            First name
            <input
              value={firstName}
              onChange={(event) => { setFirstName(event.target.value); setError(''); }}
              className="mt-2 block w-full rounded-lg border-gray-300 text-base shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Last name
            <input
              value={lastName}
              onChange={(event) => { setLastName(event.target.value); setError(''); }}
              className="mt-2 block w-full rounded-lg border-gray-300 text-base shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </label>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Phone number</label>
          <PhoneInput
            value={phone}
            onChange={(value) => { setPhone(value); setError(''); }}
            placeholder="Enter your phone number"
            required
            useTwilioValidation
          />
        </div>
      </div>
    </div>
  );

  const renderNoteStep = () => (
    <div>
      <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">One last thing</p>
      <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-bold tracking-tight text-gray-900 outline-none">
        Anything you’d like to add?
      </h2>
      <p className="mt-2 text-base text-gray-500">This note is optional and goes directly to the host.</p>
      <textarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        rows={5}
        maxLength={500}
        placeholder={selectedStatus === 'notattending' ? 'Send the host a note...' : 'Write a message...'}
        className="mt-8 block w-full resize-none border-0 border-b-2 border-gray-300 bg-transparent px-0 py-3 text-xl text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-0"
      />
      {selectedStatus === 'attending' && (
        <label className="mt-8 flex cursor-pointer items-start rounded-xl bg-gray-50 p-4">
          <input
            type="checkbox"
            checked={publicGuestView}
            onChange={(event) => setPublicGuestView(event.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-3 text-sm text-gray-700">
            <span className="block font-medium">Show my name on the guest list</span>
            <span className="mt-0.5 block text-gray-500">You’ll appear as “{firstName || 'First'} {(lastName || 'L').charAt(0).toUpperCase()}.”</span>
          </span>
        </label>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep.type) {
      case 'status': return renderStatusStep();
      case 'party': return renderPartyStep();
      case 'question': return renderQuestionStep(currentStep.question);
      case 'contact': return renderContactStep();
      case 'note': return renderNoteStep();
    }
  };

  const isFinalStep = currentStepIndex === steps.length - 1;
  const nextLabel = isFinalStep
    ? (isSubmitting ? 'Sending RSVP…' : 'Send RSVP')
    : currentStep.type === 'question'
      && !currentStep.question.required
      && !isValidGuestQuestionAnswer(currentStep.question, guestQuestionAnswers[currentStep.question.id])
      ? 'Skip'
      : 'Continue';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/55 p-0 sm:p-4">
      <div className="relative flex h-full w-full flex-col overflow-hidden bg-white sm:h-[min(720px,92vh)] sm:max-w-lg sm:rounded-2xl sm:shadow-2xl">
        <header className="border-b border-gray-100 px-5 pb-3 pt-5 sm:px-7">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">{event.event_name}</p>
              <p className="mt-0.5 text-xs text-gray-500">
                {selectedStatus ? `Step ${currentStepIndex + 1} of ${steps.length}` : 'RSVP'}
              </p>
            </div>
            <button type="button" onClick={requestClose} aria-label="Close RSVP" className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-4 h-1 overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-blue-600 transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
          </div>
        </header>

        {isComplete ? (
          <div className="rsvp-complete-enter flex flex-1 flex-col items-center justify-center px-8 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100 text-green-600">
              <svg className="h-11 w-11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path className="rsvp-check-draw" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">You’re all set!</h2>
            <p className="mt-2 text-gray-500">{completionMessage}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-x-hidden overflow-y-auto px-6 py-8 sm:px-10 sm:py-10">
              <div key={`${currentStep.id}-${transitionKey}`} className={direction === 'forward' ? 'rsvp-step-forward' : 'rsvp-step-back'}>
                {renderCurrentStep()}
                {error && <p className="mt-5 text-sm font-medium text-red-600" role="alert">{error}</p>}
              </div>
            </div>

            <footer className="border-t border-gray-100 bg-white px-5 py-4 sm:px-7">
              <div className="flex items-center gap-3">
                {currentStepIndex > 0 && (
                  <button type="button" onClick={goBack} className="rounded-xl border border-gray-300 px-5 py-3 font-medium text-gray-700 hover:bg-gray-50">
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={goNext}
                  disabled={isSubmitting || (currentStep.type === 'status' && !selectedStatus)}
                  className="flex-1 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {currentStep.type === 'status' && !selectedStatus ? 'Choose an answer' : nextLabel}
                </button>
              </div>
            </footer>
          </>
        )}

        {showCloseConfirmation && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/45 px-6">
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="discard-rsvp-title"
              aria-describedby="discard-rsvp-description"
              className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 text-amber-700" aria-hidden="true">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 4h.01M10.3 3.7L2.6 17a2 2 0 001.7 3h15.4a2 2 0 001.7-3L13.7 3.7a2 2 0 00-3.4 0z" />
                </svg>
              </div>
              <h2 id="discard-rsvp-title" className="mt-4 text-xl font-bold text-gray-900">Leave without sending?</h2>
              <p id="discard-rsvp-description" className="mt-2 text-sm leading-6 text-gray-600">
                Your RSVP changes haven’t been sent to the host yet.
              </p>
              <div className="mt-6 grid gap-3">
                <button
                  type="button"
                  onClick={() => setShowCloseConfirmation(false)}
                  className="rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700"
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl px-4 py-3 font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                >
                  Discard changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
